import { useEffect, useState } from 'react'
import { usePageMeta } from '../../hooks/usePageMeta'
import api from '../../services/api'
import { Document } from '../../types'
import DocumentCard from './components/DocumentCard'
import CategoryTabs from './components/CategoryTabs'
import './Project.css'

function Project() {
  usePageMeta({
    title: 'Le projet',
    description: 'Consultez nos documents de campagne et téléchargez nos propositions détaillées.',
  })

  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState('all')

  useEffect(() => {
    fetchDocuments()
  }, [])

  const fetchDocuments = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await api.get<Document[]>('/documents?status=published')
      // Sort by position ascending
      const sortedDocuments = data.sort((a, b) => a.position - b.position)
      setDocuments(sortedDocuments)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des documents')
    } finally {
      setLoading(false)
    }
  }

  // Get filtered documents based on active category
  const filteredDocuments = activeCategory === 'all'
    ? documents
    : documents.filter(doc => doc.category === activeCategory)

  // Get document counts by category
  const categoryCounts = documents.reduce((acc, doc) => {
    acc[doc.category] = (acc[doc.category] || 0) + 1
    return acc
  }, {} as { [key: string]: number })

  if (loading) {
    return (
      <div className="project-container">
        <div className="project-header">
          <h1>Notre projet</h1>
          <p>Consultez nos documents de campagne</p>
        </div>
        <div className="project-loading">
          <div className="spinner"></div>
          <p>Chargement des documents...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="project-container">
        <div className="project-header">
          <h1>Notre projet</h1>
          <p>Consultez nos documents de campagne</p>
        </div>
        <div className="project-error">
          <p>Une erreur est survenue lors du chargement des documents.</p>
          <p className="error-message">{error}</p>
          <button onClick={fetchDocuments} className="retry-button">
            Réessayer
          </button>
        </div>
      </div>
    )
  }

  if (documents.length === 0) {
    return (
      <div className="project-container">
        <div className="project-header">
          <h1>Notre projet</h1>
          <p>Consultez nos documents de campagne</p>
        </div>
        <div className="project-empty">
          <p>Aucun document n'est disponible pour le moment.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="project-container">
      <div className="project-header">
        <h1>Notre projet</h1>
        <p>Consultez nos documents de campagne et téléchargez nos propositions détaillées</p>
      </div>

      <CategoryTabs
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        categoryCounts={categoryCounts}
      />

      {filteredDocuments.length === 0 ? (
        <div className="project-empty">
          <p>Aucun document dans cette catégorie.</p>
        </div>
      ) : (
        <div className="documents-grid">
          {filteredDocuments.map((document) => (
            <DocumentCard key={document.id} document={document} />
          ))}
        </div>
      )}
    </div>
  )
}

export default Project
